import React from 'react';

import { US_STATES } from '@constants/constants';

import AddressAutocomplete from './AddressAutocomplete';
import InputField from './InputField';
import SelectField from './SelectField';

type Address = { street?: string; street2?: string; city?: string; state?: string; zip?: string };

type Props = {
  // Field path prefix (e.g., "address" or "billingAddress")
  fieldPrefix: string;
  // Label prefix (e.g., "" or "Billing") - optional, defaults to empty
  labelPrefix?: string;
  // Address values
  address: Address;
  // Update function
  update: (path: string, val: unknown) => void;
  // Error handling
  showErrors: boolean;
  errors: Record<string, string>;
  // Optional: Custom state validation function
  stateValidation?: (state: string) => { showError: boolean; error: string };
  // Optional: Container className - defaults to 'form-grid'
  containerClassName?: string;
};

export default function AddressFormFields({
  fieldPrefix,
  labelPrefix = '',
  address,
  update,
  showErrors,
  errors,
  stateValidation,
  containerClassName,
}: Props) {
  // Generate field IDs
  const streetId = `${fieldPrefix}.street`;
  const street2Id = `${fieldPrefix}.street2`;
  const cityId = `${fieldPrefix}.city`;
  const stateId = `${fieldPrefix}.state`;
  const zipId = `${fieldPrefix}.zip`;

  // Generate labels with optional prefix
  const streetLabel = labelPrefix ? `${labelPrefix} Street Address` : 'Street Address';
  const street2Label = labelPrefix ? `${labelPrefix} Street Address 2 (optional)` : 'Street Address 2 (optional)';
  const cityLabel = labelPrefix ? `${labelPrefix} City` : 'City';
  const stateLabel = labelPrefix ? `${labelPrefix} State` : 'State';
  const zipLabel = labelPrefix ? `${labelPrefix} ZIP Code` : 'ZIP Code';

  // Get state validation result
  const getStateValidation = () => {
    if (stateValidation && address.state) {
      return stateValidation(address.state);
    }
    return {
      showError: showErrors && !!errors[stateId],
      error: errors[stateId] || '',
    };
  };

  const stateValidationResult = getStateValidation();

  return (
    <div className={`form-grid ${containerClassName}`}>
      {/* Street Address Field */}
      <div className='field field-span-2'>
        <div className='po-field'>
          <label className='po-label' htmlFor={streetId}>
            {streetLabel}
            <span className='po-required'>*</span>
          </label>
          <AddressAutocomplete
            id={streetId}
            name={streetId}
            value={address.street || ''}
            placeholder='123 Main St'
            ariaInvalid={!!(showErrors && errors[streetId])}
            onChange={val => update(streetId, val)}
            onPlaceSelected={selectedAddress => {
              update(`${fieldPrefix}.street`, selectedAddress.street);
              update(`${fieldPrefix}.city`, selectedAddress.city);
              update(`${fieldPrefix}.state`, selectedAddress.state);
              update(`${fieldPrefix}.zip`, selectedAddress.zip);
            }}
          />
          {showErrors && errors[streetId] ? <div className='po-error inline'>{errors[streetId]}</div> : null}
        </div>
      </div>

      {/* Street Address 2 Field */}
      <div className='field field-span-2'>
        <InputField
          id={street2Id}
          label={street2Label}
          value={address.street2 || ''}
          onChange={val => update(street2Id, val)}
          placeholder='Apt, suite, unit, building, floor, etc.'
        />
      </div>

      {/* City Field */}
      <div className='field'>
        <InputField
          id={cityId}
          label={cityLabel}
          required
          value={address.city || ''}
          onChange={val => update(cityId, val)}
          showError={showErrors && !!errors[cityId]}
          error={errors[cityId]}
        />
      </div>

      {/* State Field */}
      <div className='field'>
        <SelectField
          id={stateId}
          label={stateLabel}
          required
          value={address.state || ''}
          options={US_STATES}
          onChange={val => update(stateId, val)}
          showError={stateValidationResult.showError}
          error={stateValidationResult.error}
        />
      </div>

      {/* ZIP Code Field */}
      <div className='field'>
        <InputField
          id={zipId}
          label={zipLabel}
          required
          placeholder='12345'
          value={address.zip || ''}
          onChange={val => update(zipId, val)}
          showError={showErrors && !!errors[zipId]}
          error={errors[zipId]}
        />
      </div>
    </div>
  );
}
